import { the } from '../core/words.js';
import { ft } from '../core/units.js';
import { POSSIBLE } from './prompts.js';
import { OBJECTS } from '../data/objects.js';
import { PRANKS, PRANK_KIND, PRANK_STOCK, byId } from '../data/pranks.js';

/**
 * What the action key offers you when you are carrying a nuisance.
 *
 * Every row is an ordinary candidate with an ordinary `run`, offered into the
 * same list as picking a chair up or having a word with somebody, and sorted by
 * the same rules. There is no prank menu and no prank key: a rubber band in
 * your hands is a thing the key can do something with, exactly like an armful
 * of linen is.
 *
 * The one thing worth saying about the whole file is what it does *not* do.
 * Nothing here checks whether anybody is looking. Not once, for any of the
 * eight. The act goes out and perception answers, which is the only reason
 * "unwitnessed is free" can be a property of the game rather than a promise
 * repeated in eight places.
 */

/** Feet. How close you have to be to a stock point to help yourself. */
const STOCK_REACH = 6;

/**
 * How far round behind somebody counts as behind them.
 *
 * Ice down a shirt is a from-behind action and this is that sentence as a
 * number: the target's own facing, against the direction you are standing in.
 * Zero would be exactly at their shoulder, so a little negative means properly
 * behind — a quarter turn either side of straight up their back.
 */
const BEHIND = -0.25;

export function createPrankOffers({
  pranks,
  carry,
  player,
  rig,
  npcs,
  level,
  hud,
  autosave,
  propAt,
  unavailable = () => false,
}) {
  const near = (a, b) => Math.hypot(a.x - b.x, a.z - b.z);

  /**
   * How far you are from a prop, measured to the thing rather than to its
   * middle.
   *
   * The same rule `interaction.js` keeps: a twelve-foot shelf is usable from
   * anywhere along it. Without it, standing against one end of Banquet Storage
   * is seven feet from the centre of a six-foot reach and the shelf refuses to
   * hand you anything while you are leaning on it.
   */
  function reachTo(prop, point) {
    const dx = Math.max(0, Math.abs(point.x - prop.x) - (prop.halfX ?? 0));
    const dz = Math.max(0, Math.abs(point.z - prop.z) - (prop.halfZ ?? 0));
    return Math.hypot(dx, dz);
  }

  /** Whatever is in your hands this frame, prank or otherwise. */
  function holding() {
    return carry.kind;
  }

  /** The prank you are carrying, if what you are carrying is one. */
  function carried() {
    const kind = holding();
    if (!kind) return null;
    return PRANKS.find((prank) => PRANK_KIND[prank.id] === kind) ?? null;
  }

  /**
   * Both of them, when one object is two pranks.
   *
   * A handful of ice is ice down a collar or ice on a floor, and which one it
   * is depends entirely on what you press. Nothing else in the catalogue
   * shares a kind, and this is the one line that copes with it.
   */
  function carriedAll() {
    const kind = holding();
    if (!kind) return [];
    return PRANKS.filter((prank) => PRANK_KIND[prank.id] === kind);
  }

  function nearestPerson(reachFeet) {
    let best = null;
    for (const npc of npcs) {
      if (!npc.present) continue;
      const distance = near(player.position, npc.position);
      if (distance > ft(reachFeet)) continue;
      if (!best || distance < best.distance) best = { npc, distance };
    }
    return best;
  }

  /** Whether you are behind them, as they are standing this frame. */
  function behind(npc) {
    const dx = player.position.x - npc.position.x;
    const dz = player.position.z - npc.position.z;
    const length = Math.hypot(dx, dz) || 1;
    const facing = (dx / length) * Math.sin(npc.yaw) + (dz / length) * Math.cos(npc.yaw);
    return facing <= BEHIND;
  }

  /**
   * Helping yourself at a stock point.
   *
   * The ice machine, the manager's desk, the pass, the walk-in, the shelf in
   * storage. It is `carry.take`, which is the same call the round shelf makes,
   * so taking a handful of ice puts down whatever you were carrying — and that
   * is the cost of the walk, without anybody having written a rule about it.
   */
  function offerStock() {
    if (unavailable()) return [];
    const out = [];
    for (const point of PRANK_STOCK) {
      const prop = propAt(point.prop);
      if (!prop) continue;
      const distance = reachTo(prop, player.position);
      if (distance > ft(STOCK_REACH)) continue;
      for (const id of point.stocks) {
        const prank = byId[id];
        const kind = PRANK_KIND[id];
        // Two pranks share the ice, so the machine offers it once.
        if (out.some((row) => row.kind === kind)) continue;
        const definition = OBJECTS[kind];
        out.push({
          id: `prank-take-${kind}`,
          kind,
          /*
           * Behind the hold, never on the tap.
           *
           * Helping yourself to somebody's salt is a deliberate act and not
           * the thing the key should do because you happened to walk past a
           * counter. It is also a rule with teeth: the Chef stands at the
           * kitchen pass, which is a stock point, and at `NEAR` the two
           * candidates this puts there outranked *talking to him* — a script
           * that walked up to negotiate for chairs took a napkin instead and
           * reported that it could not get a word in. A shelf does not outrank
           * a person.
           */
          tier: POSSIBLE,
          distance,
          verb: 'take',
          what: `${definition.name.toLowerCase()} from the ${point.label.toLowerCase()}`,
          run() {
            const took = carry.take(kind);
            hud.say(
              took.returned
                ? `${took.count} × ${definition.name.toLowerCase()}. Put ${the(OBJECTS[took.returned.kind].name.toLowerCase())} down.`
                : `${took.count} × ${definition.name.toLowerCase()}.`,
              { tone: 'info', seconds: 2 }
            );
            autosave.touch();
          },
        });
        void prank;
      }
    }
    return out;
  }

  /** The three you do to a person: ice, a peppered napkin, a note. */
  function offerOnPerson() {
    if (unavailable()) return [];
    const out = [];
    for (const prank of carriedAll()) {
      if (prank.shape !== 'done') continue;
      const target = nearestPerson(prank.reach);
      if (!target) continue;
      /*
       * Behind them, for the two that are. Offered-and-blocked rather than
       * hidden, because a prompt that silently is not there teaches nobody
       * that the rule exists.
       */
      const fromBehind = prank.id !== 'pepper';
      const blocked = fromBehind && !behind(target.npc) ? 'they are facing you' : null;
      out.push({
        id: `prank-do-${prank.id}`,
        tier: POSSIBLE,
        distance: target.distance,
        verb: prank.verb,
        what: target.npc.data.name,
        blocked,
        run() {
          const done = pranks.doTo(prank.id, target.npc.data.id, { from: { ...player.position } });
          if (!done.ok) {
            hud.say(done.reason, { tone: 'warn', seconds: 2 });
            return;
          }
          carry.spend();
          autosave.touch();
        },
      });
    }
    return out;
  }

  /** The two you throw. Aim is the camera, the same as every other throw. */
  function offerThrown() {
    if (unavailable()) return [];
    const out = [];
    for (const prank of carriedAll()) {
      if (prank.shape !== 'thrown') continue;
      out.push({
        id: `prank-fling-${prank.id}`,
        tier: POSSIBLE,
        distance: 0.02,
        verb: prank.verb.split(' ')[0],
        what: prank.name.toLowerCase(),
        run() {
          const from = { x: player.position.x, y: player.position.y + 1.4, z: player.position.z };
          pranks.fling(prank.id, { from, yaw: rig.yaw });
          /*
           * A rubber band is not used up by being fired, which is the one rule
           * its row declares and the only place it is read. Everything else
           * spends, like everything else in the game does.
           */
          if (!prank.endless) carry.spend();
          autosave.touch();
        },
      });
    }
    return out;
  }

  /** The two you leave on a floor. */
  function offerLeft() {
    if (unavailable()) return [];
    const out = [];
    for (const prank of carriedAll()) {
      if (prank.shape !== 'left') continue;
      const room = level.roomAt(player.position.x, player.position.z);
      out.push({
        id: `prank-leave-${prank.id}`,
        tier: POSSIBLE,
        distance: 0.03,
        verb: prank.verb.split(' ')[0],
        what: room ? `here, in the ${room.name.toLowerCase()}` : 'here',
        run() {
          pranks.leave(prank.id, { x: player.position.x, z: player.position.z });
          carry.spend();
          hud.say(`${prank.name}. Left where it will be found.`, { tone: 'info', seconds: 3 });
          autosave.touch();
        },
      });
    }
    return out;
  }

  /** And the one you do to a thing that goes off later. */
  function offerDoctor() {
    if (unavailable()) return [];
    const out = [];
    for (const prank of carriedAll()) {
      if (prank.shape !== 'doctored') continue;
      const prop = propAt(prank.into);
      if (!prop) continue;
      const distance = reachTo(prop, player.position);
      if (distance > ft(prank.reach)) continue;
      out.push({
        id: `prank-doctor-${prank.id}`,
        // Behind the hold, for the same reason as the shelves above.
        tier: POSSIBLE,
        distance,
        verb: prank.verb,
        what: 'the sugar caddy',
        run() {
          pranks.doctor(prank.id, prop);
          carry.spend();
          hud.say('Nobody is going to notice that until somebody has a coffee.', {
            tone: 'info',
            seconds: 4,
          });
          autosave.touch();
        },
      });
    }
    return out;
  }

  return {
    offerPranks() {
      return [
        ...offerStock(),
        ...offerOnPerson(),
        ...offerThrown(),
        ...offerLeft(),
        ...offerDoctor(),
      ];
    },
    /** What you are carrying, for the readouts. Null unless it is a nuisance. */
    carried,
    behind,
  };
}
